<template>
  <div class="dialogue-modal-mask">
    <div class="dialogue-modal-wrapper">
      <div class="dialogue-modal-container" ref="containerRef">
        <div class="dialogue-overlay">
          <div class="dialogue dialogue-modal-content">
            <header class="dialogue-modal-header" ref="headerRef">
              <h3 v-if="!options.hideTitle">{{ options.title }}</h3>
            </header>
            <icon
              v-if="closeBtn"
              icon="x-bold fa-close"
              alt="Close modal button"
              class="close-btn"
              aria-label="Close"
              @click.prevent="$emit('close')"
            />
            <div class="dialogue-msg dialogue-modal-description" ref="descriptionRef">
              <div v-html="DOMPurify.sanitize(options.description || '')"></div>
            </div>
            <div class="footer dialogue-modal-footer" :class="footerStyle" ref="footerRef">
              <button
                v-for="btn in options.buttons"
                :key="btn.label"
                :class="[
                  btn.classStyle,
                  { isLoading: btn.type === ButtonTypes.Confirm && loading },
                ]"
                :disabled="loading"
                @click.prevent="btn.onClick"
              >
                <span>{{ btn.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, withDefaults, defineExpose, ref } from "vue";
import { ButtonTypes, Button as ButtonVM } from "@/models/modal";
import DOMPurify from "dompurify";
import Icon from "@/components/shared/Icon.vue";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    options: { hideTitle?: boolean; title?: string; description?: string; buttons?: ButtonVM[] };
    footerStyle?: "center" | "left" | "right";
    closeBtn?: boolean;
  }>(),
  {
    loading: false,
    footerStyle: "center",
    closeBtn: false,
  },
);

const containerRef = ref<HTMLDivElement | null>();
const descriptionRef = ref<HTMLDivElement | null>();
const footerRef = ref<HTMLDivElement | null>();
const headerRef = ref<HTMLDivElement | null>();

defineExpose({
  props,
  containerRef,
  descriptionRef,
  footerRef,
  headerRef,
});
</script>

<style scoped lang="scss">
* {
  --defaultTextColor: #000;
  --defaultBtnColor: #000;
  --defaultBtnTextColor: #fff;
}

.dialogue-modal-mask {
  position: fixed;
  z-index: var(--tooltip-z-index, 9998);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.dialogue-modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.dialogue-modal-container {
  width: 76%;
  max-width: 800px;
  margin: 0px auto;
  padding: 0;
  background-color: #fff;
  border-radius: 0.6rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  max-height: 600px;
  text-align: center;
  color: var(--defaultTextColor);
  padding-top: 10px;
  position: relative;
}

.dialogue-modal-header {
  width: 100%;
  border-bottom: 0;
  padding: 0.5px;
  border-top-left-radius: 0.3rem;
  border-top-right-radius: 0.3rem;
  display: flex;
  justify-content: center;
}

.dialogue-modal-header h3 {
  color: #000;
  font-family: "Montserrat";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.dialogue-modal .modal-title {
  margin-top: 1rem;
  padding: 1.563rem 1.5rem;
  width: 100%;
}

.dialogue-modal .close-btn {
  margin: 0 0 -1rem auto;
  padding: 1rem;
}

.dialogue-modal .close-btn img {
  height: 18px;
  width: 18px;
}

.dialogue-ovelay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999999;
}

.dialogue-ovelay .dialogue {
  width: 400px;
  margin: 100px auto 0;
  background-color: #fff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  overflow: hidden;
  padding: 10px 8px;
}

.dialogue-ovelay .dialogue header {
  padding: 10px 8px;
  background-color: #f6f7f9;
  border-bottom: 1px solid #e5e5e5;
}

.dialogue-ovelay .dialogue header h3 {
  font-size: 14px;
  margin: 0;
  color: #555;
  display: inline-block;
}

.dialogue-ovelay .dialogue header .fa-close {
  float: right;
  color: #c4c5c7;
  cursor: pointer;
  transition: all 0.5s ease;
  padding: 0 2px;
  border-radius: 1px;
}

.dialogue-ovelay .dialogue header .fa-close:hover {
  color: #b9b9b9;
}

.dialogue-ovelay .dialogue header .fa-close:active {
  box-shadow: 0 0 5px #673ab7;
  color: #a2a2a2;
}

.dialogue-ovelay .dialogue .dialogue-msg {
  padding: 12px 10px;
}

.dialogue-ovelay .dialogue .dialogue-msg p {
  margin: 0;
  font-size: 15px;
  color: #333;
}

.dialogue-ovelay .dialogue footer {
  border-top: 1px solid #e5e5e5;
  padding: 8px 10px;
}

.dialogue-ovelay .dialogue footer .controls {
  direction: rtl;
}

.dialogue-ovelay .dialogue footer .controls .button {
  padding: 5px 15px;
  border-radius: 3px;
}

// Footer
.footer-btns {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 75px;
  width: 100%;
}

.footer-btns button:not(:first-child) {
  margin-left: 10px;
}

.button {
  cursor: pointer;
}

.button-default {
  background-color: rgb(248, 248, 248);
  border: 1px solid rgba(204, 204, 204, 0.5);
  color: #5d5d5d;
}

.button-danger {
  background-color: #f44336;
  border: 1px solid #d32f2f;
  color: #f5f5f5;
}

.link {
  padding: 5px 10px;
  cursor: pointer;
}

.footer {
  display: flex;
  gap: 40px;
}
.footer.center {
  justify-content: center;
}

.footer.left {
  justify-content: flex-start;
}

.footer.right {
  justify-content: flex-end;
}

.footer button {
  background-color: #000;
  color: #fff;
  gap: 12px;
  justify-content: center;
}

.footer button:not(:disabled):hover {
  filter: brightness(90%);
  transform: background-color 0.5s;
}

.cancel-btn {
  display: flex;
  width: 114px !important;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #f2f3f5 !important;
  color: #000000 !important;
  cursor: pointer;
  border: 1px solid #f2f3f5;
  font-weight: 600;
}

.exit-btn {
  display: flex;
  width: 114px !important;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #000;
  color: #fff;
  cursor: pointer;
  border: none;
  font-weight: 600;
}

.close-btn {
  font-weight: 600;
  color: #000;
  cursor: pointer;
  position: absolute;
  top: 8px;
  right: 0;
  padding: 0 8px;
}
</style>
<style scoped src="@/styles/dialogue-modal.css"></style>
